import { format, formatDistanceToNow } from "date-fns";
import { ExternalLink, FileText } from "lucide-react";
import { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { Text } from "~/components/text";
import { cn } from "~/utils";
import { ManuscriptProcessingReportWithId } from "~/integrations/firebase/collections/manuscript-processing-reporting";
import { ManuscriptProcessingCopyButton } from "./manuscript-processing-copy-button";
import { ManuscriptProcessingOriginalEntry } from "./manuscript-processing-original-entry";
import { ManuscriptProcessingRow } from "./manuscript-processing-row";
import { ManuscriptProcessingStatusBadge } from "./manuscript-processing-status-badge";
import { getStatusPresentation } from "~/pages/settings/organisations/organisation/manuscript-processing/utils/manuscript-processing-status-presentation";
import { ManuscriptProcessingSummary } from "./manuscript-processing-summary";
import { reportFileName } from "~/pages/settings/organisations/organisation/manuscript-processing/utils/manuscript-processing-report";

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="px-4 py-5">
    <Text as="h3" variant="overline" className="mb-1">
      {title}
    </Text>
    {children}
  </section>
);

const DetailRow = ({
  label,
  value,
  copyLabel,
  copiedMessage,
  mono,
  wrap,
}: {
  label: string;
  value: string;
  copyLabel?: string;
  copiedMessage?: string;
  mono?: boolean;
  wrap?: boolean;
}) => (
  <ManuscriptProcessingRow label={label} align={wrap ? "start" : "center"}>
    <Text
      variant={mono ? "caption" : "body"}
      numeric={mono ? "mono" : "default"}
      className={cn("min-w-0 grow", wrap ? "break-all" : "truncate")}
      title={value}
    >
      {value}
    </Text>
    {copyLabel && copiedMessage && (
      <ManuscriptProcessingCopyButton
        value={value}
        label={copyLabel}
        copiedMessage={copiedMessage}
      />
    )}
  </ManuscriptProcessingRow>
);

export const ManuscriptProcessingReportSheet = ({
  report,
  open,
  onOpenChange,
  onOpenOriginal,
}: {
  report: ManuscriptProcessingReportWithId | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenOriginal: (reportId: string) => void;
}) => {
  const { t } = useTranslation("settings");

  if (!report) {
    return null;
  }

  const { error } = report;
  const fileName = reportFileName(report);
  const progressedAt =
    report.updatedAt &&
    report.createdAt &&
    report.updatedAt.getTime() !== report.createdAt.getTime()
      ? report.updatedAt
      : null;
  const dotClassName =
    getStatusPresentation(report.status)?.timelineDotClassName ??
    "bg-secondary-300";

  // The year has to be spelled out: the relative half stops carrying it once
  // an entry is old enough to read as "over 2 years ago".
  const timestamp = (date: Date) =>
    `${format(date, "d MMM yyyy, HH:mm")} · ${formatDistanceToNow(date, {
      addSuffix: true,
    })}`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="manuscript-processing-report-sheet"
        // Overrides the brand inset drawer: a floating edge over the cards
        // behind it reads as a second surface.
        className="inset-y-0 gap-0 rounded-none p-0 ease-out data-[state=closed]:duration-200 data-[state=open]:duration-300 sm:max-w-lg"
      >
        <SheetHeader className="flex-row items-start gap-3 border-b border-secondary-100 p-4 pr-12">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-secondary-200">
            <FileText aria-hidden className="size-4 text-secondary-400" />
          </div>
          <div className="flex min-w-0 flex-col items-start gap-2">
            <SheetTitle asChild>
              <Text
                as="span"
                variant="heading"
                numeric="mono"
                className="break-all"
              >
                {fileName}
              </Text>
            </SheetTitle>
            <ManuscriptProcessingStatusBadge status={report.status} />
          </div>
          <SheetDescription className="sr-only">
            {t("organisation.manuscriptProcessing.sidebar.description")}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {error && (
            <Section
              title={t("organisation.manuscriptProcessing.sidebar.error")}
            >
              <Text variant="errorSmall" numeric="mono" className="mb-1">
                {error.code}
              </Text>
              <Text variant="body">{error.message}</Text>
            </Section>
          )}

          {report.duplicateOfReportId && (
            <Section
              title={t(
                "organisation.manuscriptProcessing.sidebar.originalEntry",
              )}
            >
              <ManuscriptProcessingOriginalEntry
                reportId={report.duplicateOfReportId}
                onOpen={onOpenOriginal}
              />
            </Section>
          )}

          {report.semanticDataId && (
            <Section
              title={t("organisation.manuscriptProcessing.sidebar.summary")}
            >
              <ManuscriptProcessingSummary
                semanticDataId={report.semanticDataId}
              />
            </Section>
          )}

          <Section title={t("organisation.manuscriptProcessing.sidebar.file")}>
            <DetailRow
              label={t("organisation.manuscriptProcessing.fileName.label")}
              value={fileName}
              mono
            />
            {report.contentHash && (
              <DetailRow
                label={t("organisation.manuscriptProcessing.hash.label")}
                value={report.contentHash}
                copyLabel={t("organisation.manuscriptProcessing.hash.copy")}
                copiedMessage={t(
                  "organisation.manuscriptProcessing.hash.copied",
                )}
                mono
              />
            )}
            {report.objectPath && (
              <DetailRow
                label={t("organisation.manuscriptProcessing.objectPath.label")}
                value={report.objectPath}
                copyLabel={t(
                  "organisation.manuscriptProcessing.objectPath.copy",
                )}
                copiedMessage={t(
                  "organisation.manuscriptProcessing.objectPath.copied",
                )}
                mono
                wrap
              />
            )}
          </Section>

          <Section
            title={t("organisation.manuscriptProcessing.sidebar.productMatch")}
          >
            {report.productTitle || report.isbn ? (
              <div className="flex flex-col gap-1 pt-2">
                {report.productId ? (
                  <Link
                    to="/books/$productId"
                    params={{ productId: report.productId }}
                    title={t("organisation.manuscriptProcessing.viewProduct")}
                    className="group flex w-fit items-center gap-1.5"
                  >
                    <Text
                      as="span"
                      variant="link"
                      className="underline-offset-2 group-hover:underline"
                    >
                      {report.productTitle ?? report.isbn}
                    </Text>
                    <ExternalLink
                      aria-hidden
                      className="size-3.5 shrink-0 text-primary-600"
                    />
                  </Link>
                ) : (
                  <Text variant="label">
                    {report.productTitle ?? report.isbn}
                  </Text>
                )}
                {report.productTitle && report.isbn && (
                  <Text variant="muted" numeric="tabular">
                    {report.isbn}
                  </Text>
                )}
              </div>
            ) : (
              <Text variant="subtle" className="pt-2">
                {t("organisation.manuscriptProcessing.sidebar.noProduct")}
              </Text>
            )}
          </Section>

          {report.createdAt && (
            <Section
              title={t("organisation.manuscriptProcessing.sidebar.timeline")}
            >
              <ol className="pt-2">
                <li className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <span className="mt-1.5 size-2 shrink-0 rounded-full bg-secondary-300" />
                    {progressedAt && (
                      <span className="w-px grow bg-secondary-100" />
                    )}
                  </div>
                  <div className="pb-5">
                    <Text variant="label">
                      {t("organisation.manuscriptProcessing.sidebar.received")}
                    </Text>
                    <Text variant="muted" numeric="tabular">
                      {timestamp(report.createdAt)}
                    </Text>
                  </div>
                </li>
                {progressedAt && (
                  <li className="flex gap-3">
                    <span
                      className={`mt-1.5 size-2 shrink-0 rounded-full ${dotClassName}`}
                    />
                    <div>
                      <ManuscriptProcessingStatusBadge status={report.status} />
                      <Text variant="muted" numeric="tabular" className="mt-1">
                        {timestamp(progressedAt)}
                      </Text>
                    </div>
                  </li>
                )}
              </ol>
            </Section>
          )}

          <Section
            title={t("organisation.manuscriptProcessing.sidebar.references")}
          >
            {report.jobId && (
              <DetailRow
                label={t("organisation.manuscriptProcessing.jobId.label")}
                value={report.jobId}
                copyLabel={t("organisation.manuscriptProcessing.jobId.copy")}
                copiedMessage={t(
                  "organisation.manuscriptProcessing.jobId.copied",
                )}
                mono
              />
            )}
            {report.workId && (
              <DetailRow
                label={t("organisation.manuscriptProcessing.workId.label")}
                value={report.workId}
                copyLabel={t("organisation.manuscriptProcessing.workId.copy")}
                copiedMessage={t(
                  "organisation.manuscriptProcessing.workId.copied",
                )}
                mono
              />
            )}
            <DetailRow
              label={t(
                "organisation.manuscriptProcessing.organisationId.label",
              )}
              value={report.organisation.id}
              copyLabel={t(
                "organisation.manuscriptProcessing.organisationId.copy",
              )}
              copiedMessage={t(
                "organisation.manuscriptProcessing.organisationId.copied",
              )}
              mono
            />
          </Section>
        </div>
      </SheetContent>
    </Sheet>
  );
};
